import { useIonViewWillEnter, useIonViewWillLeave } from "@ionic/react";
import { FC, ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import Info from "../../components/atoms/ErrorComponent";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import DisplayContent from "../../components/molecules/DisplayContent";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useConfig } from "../../contexts/ConfigContext";
import { usePageBackground } from "../../hooks/usePageBackground";
import { getStudyContent } from "../../services/study.service";
import { pageBackgroundOf } from "../../theme/pageStyle";
import { StudyContentType } from "../../types/welcome";

/**
 * One resource article — the regular content read (GET /contents/{id}) rendered
 * through DisplayContent, so the article gets the full treatment for free: the
 * design-system blocks, the authored page background, and the read-aloud row.
 */
const DisplayArticle: FC = (): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { study_slug, contentId } = useParams<{ study_slug: string; contentId: string }>();
  const { study } = useConfig();
  const { setResonanceLayout } = useResonanceLayout();

  const [content, setContent] = useState<StudyContentType | null>(null);
  const [isError, setIsError] = useState(false);

  // Deps matter: tab pages stay MOUNTED across tab switches (usePageBackground's
  // mount effect won't re-fire), so re-entering must re-apply the authored page
  // background itself — other tabs set the shared layout to white on their enter.
  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      title: "resources_tab_label",
      welcomePage: false,
      backgroundColor: content ? pageBackgroundOf(content.page_style) : "white",
      // Back to the resources list (browser-history back also works mid-flow).
      headerLeft: ["back"],
      headerBackFallbackPath: `/${study_slug}/tabs/resources`,
    });
  }, [content, study_slug]);

  // The layout context is shared app-wide and most pages never touch
  // the header slots — restore the default on leave so the back arrow
  // can't haunt other tabs.
  useIonViewWillLeave(() => {
    setResonanceLayout({
      headerLeft: undefined, // project default
      headerBackFallbackPath: "",
    });
  });

  useEffect(() => {
    let active = true;
    if (!study?.id || !contentId) return undefined;
    setContent(null);
    setIsError(false);
    getStudyContent({ studyId: study.id, contentId: Number(contentId), lang: language })
      .then(({ data }) => {
        if (active) setContent(data);
      })
      .catch(() => {
        if (active) setIsError(true);
      });
    return () => {
      active = false;
    };
  }, [study?.id, contentId, language]);

  // Authored page background (page_style) applies to the article page like anywhere else.
  usePageBackground(content?.page_style);

  return (
    <ResonanceLayout>
      {isError ? (
        <Info
          title={t("common_server_error_title")}
          description={t("common_server_error_desc")}
        />
      ) : !content ? (
        <ResonanceLoader />
      ) : (
        <DisplayContent editorJsData={content.content} />
      )}
    </ResonanceLayout>
  );
};

export default DisplayArticle;
